import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { Permission } from '../types/auth';
import { securityLogService } from '../services/securityLogService.ts';
import { createLogger } from '../utils/logger.ts';

const logger = createLogger('ProtectedRoute');

interface ProtectedRouteProps {
  children: React.ReactElement;
  permissions?: Permission[];
  requireAll?: boolean;
  fallback?: React.ReactElement;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  permissions = [],
  requireAll = false,
  fallback
}) => {
  const { user, loading, hasAnyPermission, hasAllPermissions, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      logger.error('Logout error:', error instanceof Error ? error : undefined);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check permissions if specified
  if (permissions.length > 0) {
    const hasPermission = requireAll
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);

    if (!hasPermission) {
      // 🔒 P1 보안 개선: 접근 거부 로깅
      securityLogService.logRouteAccessDenied(
        user.uid,
        location.pathname,
        permissions,
        {
          userRole: user.role,
          userEmail: user.email,
          requireAll
        }
      );

      // Return fallback component if provided, otherwise show access denied
      return fallback || (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">접근 권한이 없습니다</h3>
            <p className="mt-1 text-sm text-gray-500">
              이 페이지를 볼 수 있는 권한이 없습니다.
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100 transition-colors"
              >
                홈으로 이동
              </button>
              <button
                onClick={handleLogout}
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                다른 계정으로 로그인
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  return children;
};

export default ProtectedRoute;